import { defineStore } from "pinia";
import { CloudFunction } from "./entity";
import { CloudFunctionList } from "./list.gen";
import type {
  CloudFunctionStore,
  CloudFunctionStoreState,
} from "./store.gen.d";
import { analytics, auth } from "@/firebase";
import { type JSONData } from "../../base_classes/dto.d";
import { Pagination } from "../../base_classes/pagination";
import { Query } from "../../base_classes/query";
import { paginationAndQueriesToCacheKey } from "../../base_classes/dto_list";

/// ///////////////////////////////////////////////////////////////////////////////////////////////////
/// WARNING! THIS FILE IS AUTOMATICALLY GENERATED, ANY CHANGES WILL BE LOST ON NEXT GENERATION
/// IF YOU WANT TO EDIT THIS FILE, REMOVE THE .gen. FROM THE FILENAME. THE FILE WILL THEN BE IGNORED.
/// ///////////////////////////////////////////////////////////////////////////////////////////////////

const cloudFunctionList = new CloudFunctionList();
export const useCloudFunctionStore = defineStore("cloudFunctionStore", {
  state: (): CloudFunctionStoreState => ({
    activeID: null,
    cache: {},
    list: [] as CloudFunction[],
    retrievalTimestamp: 0,
    updateKey: 0,
    storeUpdateTimeout: null,
  }),
  actions: {
    initializeStore(): void {
      this.convertList();
    },
    emitFormUpdateEvent(id: string): void {
      window.dispatchEvent(
        new CustomEvent("form-update-CloudFunction", { detail: { id } }),
      );
    },
    emitStoreUpdateEvent(): void {
      // Clear existing timeout
      if (this.storeUpdateTimeout !== null) {
        clearTimeout(this.storeUpdateTimeout);
      }

      // Set new timeout
      this.storeUpdateTimeout = setTimeout(() => {
        window.dispatchEvent(new CustomEvent("store.update.CloudFunction"));
        this.storeUpdateTimeout = null;
      }, 1500);
    },
    async activate(cloudFunction: CloudFunction): Promise<void> {
      // Set active
      await cloudFunction
        .activate(auth.firebaseUser?.uid)
        .then(() => {
          this.activeID = cloudFunction.id;
        })
        .catch((error) => {
          console.error(error);
          this.activeID = null;
        });
    },
    add(cloudFunction: CloudFunction): void {
      const id = cloudFunction.id;
      if (typeof id === "string" && !this.contains(id)) {
        this.list.push(cloudFunction);

        // Set active
        if (this.active === null) {
          this.activeID = cloudFunction.id;
        }
      } else {
        this.reinsert(cloudFunction);
      }
      this.updateKey += 1;
    },
    convertList(): void {
      this.list = (this.list as CloudFunction[]).map((x: any) => {
        const cloudFunction = new CloudFunction({});
        cloudFunction.set(x as JSONData);
        return cloudFunction;
      });
    },
    async delete(cloudFunction: CloudFunction): Promise<void> {
      await cloudFunction
        .delete()
        .then(() => {
          this.remove(cloudFunction);
        })
        .finally(() => {
          this.emitStoreUpdateEvent();
        });
    },
    async deleteById(id: string): Promise<void> {
      const cloudFunction = (this.list as CloudFunction[]).find(
        (x: CloudFunction) => x.id === id,
      );
      if (cloudFunction !== undefined) {
        await this.delete(cloudFunction);
      } else {
        throw new Error("Could not find CloudFunction with id: " + id);
      }
    },
    contains(id: string): boolean {
      return (this.list as CloudFunction[]).some(
        (x: CloudFunction) => x.id === id,
      );
    },
    get(id: string): CloudFunction | null {
      return (
        (this.list as CloudFunction[]).find(
          (x: CloudFunction) => x.id === id,
        ) || null
      );
    },
    reinsert(cloudFunction: CloudFunction): void {
      const index = (this.list as CloudFunction[]).findIndex(
        (x: CloudFunction) => x.id === cloudFunction.id,
      );
      if (index !== -1) {
        this.list = [
          ...this.list.slice(0, index),
          cloudFunction,
          ...this.list.slice(index + 1),
        ];
      } else {
        this.add(cloudFunction);
      }
      this.updateKey += 1;
    },
    remove({ id }: CloudFunction): void {
      const index = (this.list as CloudFunction[]).findIndex(
        (x: CloudFunction) => x.id === id,
      );
      if (index !== -1) {
        this.list = [
          ...this.list.slice(0, index),
          ...this.list.slice(index + 1),
        ];
      }
      this.updateKey += 1;
    },
    async retrieve(id: string, force = false): Promise<CloudFunction> {
      // Check if a refresh is required
      if (!force && !this.refreshRequired) {
        const cloudFunction = this.get(id);
        if (cloudFunction !== null) {
          return cloudFunction;
        }
      }

      // Retrieve cloudFunction
      const cloudFunction = new CloudFunction({});

      // Get cloudFunction
      await cloudFunction.get([id]).then(async (entity) => {
        this.reinsert(entity);

        // Activate cloudFunction
        await this.activate(entity);
      });

      return cloudFunction;
    },
    async retrieveAll(force = false): Promise<{
      active: string | null;
      items: CloudFunction[];
    }> {
      // Check if a refresh is required
      if (!force && !this.refreshRequired) {
        return {
          active: this.activeID,
          items: this.list as CloudFunction[],
        };
      }

      // Clear list
      while (this.list.length > 0) {
        this.list.pop();
      }

      // Retrieve list
      await cloudFunctionList.getAll().then(async (list) => {
        list.forEach((cloudFunction) => {
          this.reinsert(cloudFunction);
        });

        // Activate cloudFunction
        if (list.length > 0) {
          if (cloudFunctionList.active !== null) {
            this.activeID = cloudFunctionList.active;
          } else {
            await this.activate(list[0]);
          }
        }
      });

      // Set retrieval timestamp
      this.retrievalTimestamp = Date.now();

      // Set user property
      if (this.activeID !== null) {
        analytics.setUserProperty("cloud_function_id", this.activeID);
      }

      return {
        active: this.activeID,
        items: this.list as CloudFunction[],
      };
    },
    async retrievePaginated(
      pagination: Pagination,
      force = false,
    ): Promise<{
      pagination: Pagination;
      items: CloudFunction[];
    }> {
      // Check pagination
      let newPagination = new Pagination(pagination).deepCopy();

      // Get cachekey
      const cacheKey = pagination.toCacheKey();

      // Check if a refresh is required
      if (!force && this.cache[cacheKey]) {
        const cached = this.cache[cacheKey];
        return {
          pagination: cached.pagination.deepCopy(),
          items: cached.ids.map((id: string) => this.get(id) as CloudFunction),
        };
      }

      // Retrieve list
      const items = await cloudFunctionList
        .getPaginated([], pagination)
        .then(async ({ items, pagination }) => {
          items.forEach((cloudFunction) => {
            this.reinsert(cloudFunction);
          });

          // Set new pagination
          newPagination = pagination.deepCopy();

          return items;
        });

      // Set cache
      this.cache[cacheKey] = {
        ids: items.map((x: CloudFunction) => x.id),
        pagination: newPagination,
        retrievalTimestamp: Date.now(),
      };

      // Set retrieval timestamp
      this.retrievalTimestamp = Date.now();

      return {
        pagination: newPagination,
        items,
      };
    },
    async query(
      queries: Query[],
      pagination: Pagination,
      force = false,
    ): Promise<{
      pagination: Pagination;
      items: CloudFunction[];
    }> {
      // Check pagination
      let newPagination = new Pagination(pagination).deepCopy();

      // Get cachekey
      const cacheKey = paginationAndQueriesToCacheKey(pagination, queries);

      // Check if a refresh is required
      if (!force && this.cache[cacheKey]) {
        const cached = this.cache[cacheKey];
        return {
          pagination: cached.pagination.deepCopy(),
          items: cached.ids.map((id: string) => this.get(id) as CloudFunction),
        };
      }

      // Retrieve list
      const items = await cloudFunctionList
        .query(queries, pagination)
        .then(async ({ items, pagination }) => {
          items.forEach((cloudFunction) => {
            this.reinsert(cloudFunction);
          });

          // Set new pagination
          newPagination = pagination.deepCopy();

          return items;
        });

      // Set cache
      this.cache[cacheKey] = {
        ids: items.map((x: CloudFunction) => x.id),
        pagination: newPagination.deepCopy(),
        retrievalTimestamp: Date.now(),
      };

      // Set retrieval timestamp
      this.retrievalTimestamp = Date.now();

      return {
        pagination: newPagination,
        items: items,
      };
    },
    async search(
      query: string,
      pagination: Pagination,
      force = false,
    ): Promise<{
      pagination: Pagination;
      items: CloudFunction[];
    }> {
      // Check pagination
      let newPagination = new Pagination(pagination).deepCopy();

      // Get cachekey
      const cacheKey =
        query.replaceAll(" ", "_") + "-" + pagination.toCacheKey();

      // Check if a refresh is required
      if (!force && this.cache[cacheKey]) {
        const cached = this.cache[cacheKey];
        return {
          items: cached.ids.map((id: string) => this.get(id) as CloudFunction),
          pagination: cached.pagination.deepCopy(),
        };
      }

      // Retrieve list
      const items = await cloudFunctionList
        .search(query, pagination)
        .then(async ({ items, pagination }) => {
          items.forEach((cloudFunction) => {
            this.reinsert(cloudFunction);
          });

          // Set new pagination
          newPagination = pagination.deepCopy();

          return items;
        });

      // Set cache
      this.cache[cacheKey] = {
        ids: items.map((x: CloudFunction) => x.id),
        pagination: newPagination.deepCopy(),
        retrievalTimestamp: Date.now(),
      };

      // Set retrieval timestamp
      this.retrievalTimestamp = Date.now();

      return {
        items: items,
        pagination: newPagination,
      };
    },
    async update(cloudFunction: CloudFunction): Promise<string> {
      // Update cloudFunction
      await cloudFunction.update();

      // Reinsert cloudFunction
      this.reinsert(cloudFunction);

      // Activate cloudFunction
      if (this.active === null) {
        await this.activate(cloudFunction);
      }

      this.emitStoreUpdateEvent();

      return cloudFunction.id;
    },
  },
  getters: {
    active(): CloudFunction | null {
      if (this.activeID === null) {
        return null;
      }

      const cloudFunction = (this.list as CloudFunction[]).find(
        (x: CloudFunction) => x.id === this.activeID,
      );
      if (cloudFunction === undefined) {
        return null;
      }

      return cloudFunction;
    },
    refreshRequired(): boolean {
      return (
        this.retrievalTimestamp === 0 ||
        this.retrievalTimestamp < Date.now() - 30 * 60000 ||
        this.list.length === 0
      );
    },
  },
});

// Export a function to create a new instance of the store
export function createCloudFunctionStore(): CloudFunctionStore {
  const store = useCloudFunctionStore();
  // Run the initializeStore action when the store is created
  store.initializeStore();
  return store as unknown as CloudFunctionStore; // TODO: Fix this
}
